/**
 * GenericCASigner - Generic Remote CA Signer
 *
 * Một class duy nhất cho TẤT CẢ các Remote CA providers.
 * Cấu hình qua CAEndpointConfig.
 */

import { AbstractCASigner, OTPVerificationState } from '../AbstractCASigner';
import {
  SignProvider,
  SigningResult,
  SigningOptions,
  XmlSigningOptions,
  BatchSigningItem,
  BatchSigningResult,
  CertificateInfo,
} from '../../types';

/**
 * Cấu hình endpoints cho CA Provider
 */
export interface CAEndpointConfig {
  /** Tên provider */
  provider: SignProvider;
  /** Tên hiển thị */
  displayName: string;
  /** Server URL */
  serverUrl: string;
  /** Client ID (OAuth) */
  clientId?: string;
  /** Client Secret (OAuth) */
  clientSecret?: string;
  /** Redirect URL */
  redirectUrl?: string;
  /** Endpoints */
  endpoints: {
    /** Gửi OTP */
    requestOtp: string;
    /** Xác thực OTP */
    verifyOtp: string;
    /** OAuth authorize */
    oauthAuthorize?: string;
    /** OAuth token */
    oauthToken?: string;
    /** Lấy certificate */
    certificate: string;
    /** Ký file */
    signFile: string;
    /** Ký XML */
    signXml: string;
    /** Ký batch */
    signBatch?: string;
    /** Health check */
    health?: string;
  };
}

/**
 * Generic CA Signer - Cấu hình qua config
 */
export class GenericCASigner extends AbstractCASigner {
  readonly signProvider: SignProvider;
  readonly displayName: string;

  private endpointConfig: CAEndpointConfig;

  constructor(config: CAEndpointConfig) {
    super({
      serverUrl: config.serverUrl,
      clientId: config.clientId,
      clientSecret: config.clientSecret,
      redirectUrl: config.redirectUrl,
    });
    this.signProvider = config.provider;
    this.displayName = config.displayName;
    this.endpointConfig = config;
  }

  async requestOTP(phoneNumber: string): Promise<OTPVerificationState> {
    try {
      const response = await fetch(
        `${this.config.serverUrl}${this.endpointConfig.endpoints.requestOtp}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phoneNumber,
            clientId: this.config.clientId,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        this.otpState = {
          transactionId: data.transactionId || data.transaction_id || data.id,
          otpSent: true,
          otpExpiry: new Date(Date.now() + 5 * 60 * 1000),
          verified: false,
        };
        return this.otpState;
      }

      throw new Error('Gửi OTP thất bại');
    } catch (error: any) {
      throw new Error(`${this.displayName} OTP error: ${error.message}`);
    }
  }

  async verifyOTP(otp: string, transactionId: string): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.config.serverUrl}${this.endpointConfig.endpoints.verifyOtp}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ otp, transactionId }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.verified || data.success) {
          this.accessToken = data.accessToken || data.access_token || data.token;
          if (this.otpState) this.otpState.verified = true;
          this._isReady = true;
          return true;
        }
      }
      return false;
    } catch {
      return false;
    }
  }

  getOAuthLoginUrl(): string {
    const endpoint = this.endpointConfig.endpoints.oauthAuthorize || '/oauth/authorize';
    const params = new URLSearchParams({
      client_id: this.config.clientId || '',
      redirect_uri: this.config.redirectUrl || window.location.origin + '/callback',
      response_type: 'code',
      scope: 'sign',
    });
    return `${this.config.serverUrl}${endpoint}?${params}`;
  }

  async handleOAuthCallback(callbackUrl: string): Promise<boolean> {
    try {
      const url = new URL(callbackUrl);
      const code = url.searchParams.get('code');
      if (!code) return false;

      const tokenEndpoint = this.endpointConfig.endpoints.oauthToken || '/oauth/token';

      const response = await fetch(`${this.config.serverUrl}${tokenEndpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          code,
          client_id: this.config.clientId || '',
          client_secret: this.config.clientSecret || '',
          redirect_uri: this.config.redirectUrl || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.accessToken = data.access_token || data.accessToken;
        this._isReady = true;
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  async getCertificateInfo(): Promise<CertificateInfo | null> {
    if (!this.accessToken) return null;

    try {
      const response = await fetch(
        `${this.config.serverUrl}${this.endpointConfig.endpoints.certificate}`,
        { headers: this.getAuthHeaders() }
      );

      if (response.ok) {
        const data = await response.json();
        return {
          serialNumber: data.serialNumber || 'N/A',
          subject: data.subject || '',
          issuer: data.issuer || this.displayName,
          validFrom: new Date(data.validFrom || Date.now()),
          validTo: new Date(data.validTo || Date.now() + 365 * 24 * 60 * 60 * 1000),
          isValid: data.isValid ?? true,
        };
      }
    } catch {}
    return null;
  }

  async signPdf(pdfData: string, options?: SigningOptions): Promise<SigningResult> {
    return this.signFile(pdfData, 'document.pdf', options);
  }

  async signFile(fileData: string, fileName: string, options?: SigningOptions): Promise<SigningResult> {
    if (!this.accessToken) {
      return this.createErrorResult('Chưa xác thực. Vui lòng đăng nhập hoặc xác thực OTP.');
    }

    try {
      const response = await fetch(
        `${this.config.serverUrl}${this.endpointConfig.endpoints.signFile}`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            fileData,
            fileName,
            reason: options?.reason,
            location: options?.location,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success !== false && (data.signedFile || data.signedData)) {
          return this.createSuccessResult(data.signedFile || data.signedData, {
            signerName: data.signerName || this.displayName,
            signedAt: new Date(),
          });
        }
        return this.createErrorResult(data.error || 'Ký file thất bại');
      }

      return this.createErrorResult('Ký file thất bại');
    } catch (error: any) {
      return this.createErrorResult(error.message);
    }
  }

  async signXml(xmlContent: string, options?: XmlSigningOptions): Promise<SigningResult> {
    if (!this.accessToken) {
      return this.createErrorResult('Chưa xác thực');
    }

    try {
      const response = await fetch(
        `${this.config.serverUrl}${this.endpointConfig.endpoints.signXml}`,
        {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            xmlContent,
            signatureType: options?.signatureType || 'enveloped',
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success !== false && (data.signedXml || data.signedData)) {
          return this.createSuccessResult(data.signedXml || data.signedData);
        }
        return this.createErrorResult(data.error || 'Ký XML thất bại');
      }

      return this.createErrorResult('Ký XML thất bại');
    } catch (error: any) {
      return this.createErrorResult(error.message);
    }
  }

  async signBatch(items: BatchSigningItem[], options?: SigningOptions): Promise<BatchSigningResult> {
    if (!this.accessToken) {
      return {
        success: false,
        results: items.map((i) => ({ fileName: i.fileName, error: 'Chưa xác thực' })),
      };
    }

    const batchEndpoint = this.endpointConfig.endpoints.signBatch;
    if (!batchEndpoint) {
      // Fallback: sign each file individually
      return this.signBatchFallback(items, options);
    }

    try {
      const response = await fetch(`${this.config.serverUrl}${batchEndpoint}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          files: items,
          reason: options?.reason,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        return { success: data.success, results: data.results || [] };
      }

      return {
        success: false,
        results: items.map((i) => ({ fileName: i.fileName, error: 'Batch failed' })),
      };
    } catch (error: any) {
      return {
        success: false,
        results: items.map((i) => ({ fileName: i.fileName, error: error.message })),
      };
    }
  }

  /**
   * Fallback: Sign each file individually when batch endpoint is not available
   */
  private async signBatchFallback(
    items: BatchSigningItem[],
    options?: SigningOptions
  ): Promise<BatchSigningResult> {
    const results: { fileName: string; signedData?: string; error?: string }[] = [];

    for (const item of items) {
      const result = await this.signFile(item.content, item.fileName, options);
      results.push({
        fileName: item.fileName,
        signedData: result.signedData,
        error: result.error,
      });
    }

    const allSuccess = results.every((r) => !r.error);
    return { success: allSuccess, results };
  }
}
